import { useMemo } from 'react'
import type { ExerciseRendererProps } from './types'

/**
 * Tap-to-build rather than drag-and-drop. Dragging is fiddly on a phone and
 * hostile to assistive tech; tapping is precise, accessible, and faster.
 */
export function WordOrderExercise({
  content,
  response,
  onChange,
  locked,
}: ExerciseRendererProps<'word_order'>) {
  // Memoised on the response's array rather than on a fresh `?? []`, which
  // would be a new identity every render and rebuild the Set each time.
  const order = useMemo(() => response?.order ?? [], [response?.order])
  const used = useMemo(() => new Set(order), [order])

  // Shuffled once per exercise, seeded by content so it is stable across
  // re-renders — a bank that reshuffles mid-answer is maddening.
  const bank = useMemo(() => {
    const indices = content.tokens.map((_, i) => i)
    let seed = content.tokens.join('').length * 7919
    for (let i = indices.length - 1; i > 0; i--) {
      seed = (seed * 1103515245 + 12345) % 2147483648
      const j = seed % (i + 1)
      ;[indices[i], indices[j]] = [indices[j] as number, indices[i] as number]
    }
    return indices
  }, [content.tokens])

  const append = (index: number) => onChange({ type: 'word_order', order: [...order, index] })
  const removeAt = (position: number) =>
    onChange({ type: 'word_order', order: order.filter((_, i) => i !== position) })

  return (
    <div className="space-y-5">
      <div
        aria-label="Your sentence"
        className="flex min-h-16 flex-wrap content-start gap-2 rounded-xl border-2 border-dashed border-sand-300 p-3"
      >
        {order.length === 0 && (
          <span className="self-center text-sm text-ink-400">Tap the words below</span>
        )}
        {order.map((tokenIndex, position) => (
          <button
            key={`${tokenIndex}-${position}`}
            type="button"
            disabled={locked}
            onClick={() => removeAt(position)}
            className="min-h-10 rounded-lg bg-brand-600 px-3 text-white disabled:opacity-70"
          >
            {content.tokens[tokenIndex]}
          </button>
        ))}
      </div>

      <div aria-label="Available words" className="flex flex-wrap gap-2">
        {bank.map((tokenIndex) => (
          <button
            key={tokenIndex}
            type="button"
            disabled={locked || used.has(tokenIndex)}
            onClick={() => append(tokenIndex)}
            className="min-h-11 rounded-lg bg-white px-3 text-base ring-1 ring-sand-200 disabled:opacity-30"
          >
            {content.tokens[tokenIndex]}
          </button>
        ))}
      </div>
    </div>
  )
}
